/**
 * @fileoverview Input/output schemas for the draft-blender-v1 agent.
 *
 * This agent takes multiple existing drafts of the same chapter and produces
 * a single "best of breed" blended version by lifting the strongest elements
 * of each draft while minimizing weaknesses.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// DRAFT CANDIDATE SCHEMA
// =============================================================================

/**
 * A single candidate draft to be blended.
 */
export const BlendDraftSchema = z.object({
  label: z.string().describe('Short identifier for the draft (e.g. model name or display label). Referenced by the agent in its commentary.'),
  commentary: z.string().optional().describe("The draft's own commentary / summary of choices, if one was produced."),
  wordCount: z.number().optional().describe('Word count of the draft, for the agent to reason about pacing.'),
  evaluationSummary: z.string().optional().describe('Summary of prior AI evaluation notes for this draft (winner status, committee votes, justifications) if an evaluation has been run.'),
  content: z.string().describe('The full HTML content of the draft.'),
});

export type BlendDraft = z.infer<typeof BlendDraftSchema>;

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.styleGuide.schema)
  .merge(AGENT_SCHEMA_OBJECTS.charactersCurrent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.chapterContent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.chapterContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.writingStyleSample.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.draftingHistory.schema)
  .extend({
    drafts: z.array(BlendDraftSchema).min(2).describe('The candidate drafts to blend. At least two are required.'),
  });

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  commentary: z.string().describe(
    "Brief commentary (2-4 sentences) explaining the blending approach: which draft contributed which strengths, " +
    "which weaknesses were trimmed, and any notable stylistic choices. This will be recorded in the drafting history."
  ),
  content: z.string().describe(
    'The fully blended chapter draft, formatted as a single HTML string.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
